import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import useSEO from "@/lib/useSEO";

export default function Register() {
  useSEO({ title: "Open an account", description: "Create your y ℶα account — accept payments, send P2P, hold multi-currency. Free to start.", path: "/register" });
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("customer");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { name, email, password, role });
      setUser(data.user);
      toast.success("Welcome to y ℶα.");
      navigate(role === "merchant" ? "/merchant" : "/customer", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + `/auth/callback?role=${role}`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <main data-testid="register-page" className="min-h-screen flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <div className="text-center mb-12">
          <Link to="/" className="font-display text-3xl gold-text">y ℶα</Link>
          <h1 className="font-display text-4xl mt-8 tracking-tight">Open an account.</h1>
          <p className="text-secondary-fg mt-2 text-sm">A private vault, made for movement.</p>
        </div>

        <div className="mb-8">
          <label className="text-xs uppercase tracking-[0.25em] text-muted-fg">I am a</label>
          <div className="grid grid-cols-2 gap-3 mt-3">
            {[
              { v: "customer", label: "Patron" },
              { v: "merchant", label: "Merchant" },
            ].map((r) => (
              <button
                key={r.v}
                type="button"
                data-testid={`role-${r.v}`}
                onClick={() => setRole(r.v)}
                className={`py-3 px-4 border text-sm transition-all ${
                  role === r.v
                    ? "border-gold text-gold bg-[rgba(212,175,55,0.05)]"
                    : "hairline text-secondary-fg hover:text-white"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={submit} className="space-y-7">
          <div>
            <label className="text-xs uppercase tracking-[0.25em] text-muted-fg">Full name</label>
            <input data-testid="register-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Sterling" className="lux-input mt-2" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.25em] text-muted-fg">Email</label>
            <input data-testid="register-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@valiant.io" className="lux-input mt-2" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.25em] text-muted-fg">Password</label>
            <input data-testid="register-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" className="lux-input mt-2" />
          </div>
          <button data-testid="register-submit" type="submit" disabled={loading} className="btn-gold w-full">
            {loading ? "Creating…" : "Create account"}
          </button>
        </form>

        <div className="flex items-center my-8 gap-4">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-xs uppercase tracking-[0.3em] text-muted-fg">or</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        <button data-testid="register-google" onClick={googleSignIn} type="button" className="btn-ghost w-full flex items-center justify-center gap-3">
          <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.2 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.2 29.3 4 24 4 16.4 4 9.9 8.4 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.4-4.5 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.7 39.5 16.3 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.4l6.2 5.2C41 35.8 44 30.4 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
          Continue with Google
        </button>

        <p className="text-center mt-10 text-sm text-secondary-fg">
          Already a member?{" "}
          <Link to="/login" data-testid="link-login" className="text-gold hover:text-white transition-colors">Sign in</Link>
        </p>
      </div>
    </main>
  );
}
